import { Paper, Box, Typography, ToggleButton, ToggleButtonGroup, useTheme, alpha, Skeleton } from '@mui/material'
import { useState, useMemo } from 'react'
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area
} from 'recharts'
import { useTranslation } from '@i18n'

// ── Distinct, clear chart colors ──
const SUBMISSIONS_COLOR = '#3b82f6' // Vivid blue — total submissions
const RESPONDED_COLOR = '#10b981'   // Emerald green — responded (clear contrast)

const SubmissionTrendsChart = ({ data = [], isLoading = false }) => {
    const { t, language } = useTranslation()
    const theme = useTheme()
    const [timeRange, setTimeRange] = useState('30')

    // Theme-based colors
    const primaryColor = theme.palette.primary.main
    const textSecondary = theme.palette.text.secondary
    const bgPaper = theme.palette.background.paper
    const dividerColor = theme.palette.divider

    // Filter data based on time range
    const filteredData = useMemo(() => {
        if (!data || data.length === 0) return []
        const days = parseInt(timeRange)
        return data.slice(-days)
    }, [data, timeRange])

    const handleTimeRangeChange = (event, newRange) => {
        if (newRange !== null) {
            setTimeRange(newRange)
        }
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        bgcolor: alpha(bgPaper, 0.95),
                        backdropFilter: 'blur(20px)',
                        p: 2,
                        borderRadius: 2,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                        border: `1px solid ${alpha(dividerColor, 0.1)}`
                    }}
                >
                    <Typography variant="body2" fontWeight={700} color="text.primary">{label}</Typography>
                    {payload.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color }} />
                            <Typography variant="body2" sx={{ color: entry.color, fontWeight: 500 }}>
                                {entry.name}: <strong>{entry.value}</strong>
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )
        }
        return null
    }

    const hasData = filteredData.length > 0

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 4,
                height: '100%',
                background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(bgPaper, 0.9)} 0%, ${alpha(primaryColor, 0.05)} 100%)`
                    : `linear-gradient(135deg, ${bgPaper} 0%, ${alpha(primaryColor, 0.02)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(dividerColor, 0.08)}`,
                boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    boxShadow: `0 8px 40px ${alpha(primaryColor, 0.15)}`,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                        {t('dashboard.submissionTrends', 'Submission Trends')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('dashboard.trendsDescription', 'Track submission volume over time')}
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    value={timeRange}
                    exclusive
                    onChange={handleTimeRangeChange}
                    size="small"
                    sx={{
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        borderRadius: 2,
                        p: 0.5,
                        '& .MuiToggleButton-root': {
                            px: 2,
                            py: 0.5,
                            border: 'none',
                            borderRadius: '8px !important',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            color: textSecondary,
                            '&.Mui-selected': {
                                bgcolor: primaryColor,
                                color: theme.palette.primary.contrastText,
                                boxShadow: `0 2px 8px ${alpha(primaryColor, 0.4)}`,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.dark
                                }
                            },
                            '&:hover': {
                                bgcolor: alpha(primaryColor, 0.08)
                            }
                        }
                    }}
                >
                    <ToggleButton value="7">{t('dashboard.7days', '7D')}</ToggleButton>
                    <ToggleButton value="14">{t('dashboard.14days', '14D')}</ToggleButton>
                    <ToggleButton value="30">{t('dashboard.30days', '30D')}</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box sx={{ height: 300, width: '100%' }}>
                {isLoading ? (
                    <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
                ) : hasData ? (
                    <ResponsiveContainer>
                        <AreaChart data={filteredData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gradientSubmissions" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={SUBMISSIONS_COLOR} stopOpacity={0.35} />
                                    <stop offset="95%" stopColor={SUBMISSIONS_COLOR} stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gradientResponded" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={RESPONDED_COLOR} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={RESPONDED_COLOR} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={alpha(dividerColor, 0.5)} vertical={false} />
                            <XAxis
                                dataKey="date"
                                stroke={textSecondary}
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                reversed={language === 'ar'}
                            />
                            <YAxis
                                stroke={textSecondary}
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                orientation={language === 'ar' ? 'right' : 'left'}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="submissions"
                                name={t('stats.total', 'Submissions')}
                                stroke={SUBMISSIONS_COLOR}
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#gradientSubmissions)"
                                dot={false}
                                activeDot={{ r: 6, stroke: bgPaper, strokeWidth: 2, fill: SUBMISSIONS_COLOR }}
                            />
                            <Area
                                type="monotone"
                                dataKey="responded"
                                name={t('stats.replied', 'Responded')}
                                stroke={RESPONDED_COLOR}
                                strokeWidth={2.5}
                                fillOpacity={1}
                                fill="url(#gradientResponded)"
                                dot={false}
                                activeDot={{ r: 5, stroke: bgPaper, strokeWidth: 2, fill: RESPONDED_COLOR }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('common.noData', 'No data available')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    )
}

export default SubmissionTrendsChart
